/*
 * Technical Support — used by the CXO and Technical Support panels.
 * Issues by status (New / In Progress / Resolved); the status button opens the chat, and the chat
 * sends people back here with { screeName: "chat", … } so the filters are restored (same as before).
 * Admins (loginType 1) can tick issues and assign them to a service counsellor.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { UserPlus } from "lucide-react";
import { assignSupportIssue, getAdminSupport, getCRMEmployeeList, getExamListNew, getParentCourseNew } from "../../Action/courseAction";
import { CustomAdminTable, CustomBadge, CustomButton, CustomDropdowns, CustomPageLayout } from "../../Component/customComponents";
import { BatchNames, StatusDropdown, TableTitle, formatDateRange, todayISO } from "../Common/transactionCommon";
import { MobileCell } from "../Common/orderAnalysisCommon";

const STATUSES = [
  { _id: 1, name: "New Issue" },
  { _id: 2, name: "In Progress" },
  { _id: 3, name: "Resolved" },
];
const SUPPORT_TYPES = [
  { _id: 1, name: "App Related" },
  { _id: 2, name: "Course Related" },
];
const SERVICE_COUNSELLOR_ROLE = 8;
const filled = (o) => (o && o._id ? o : ""); // the chat passes { _id: undefined } when nothing was picked

const TechnicalSupportNew = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state: loc } = useLocation();
  const loginType = useSelector((s) => s?.Auth?.loginType);
  const isAdmin = loginType === 1;
  const fromChat = loc?.screeName === "chat";

  const [examList, setExamList] = useState([]);
  const [streamList, setStreamList] = useState([]);
  const [counsellors, setCounsellors] = useState([]);
  const [filters, setFilters] = useState(() => ({
    startDate: (fromChat && loc?.startDate) || todayISO(),
    endDate: (fromChat && loc?.endDate) || todayISO(),
    supportType: "",
    exam: fromChat ? filled(loc?.examTypeId) : "",
    examStream: fromChat ? filled(loc?.baseCourseId) : "",
    counsellor: fromChat ? filled(loc?.counsellorId) : "",
  }));
  const [status, setStatus] = useState(() => (fromChat && loc?.selectedStatus) || 1);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [assignTo, setAssignTo] = useState("");
  const [assigning, setAssigning] = useState(false);

  /** same request as before: { skip, limit, supportType: 1, startDate, endDate, status, technicalSupportType?, examTypeId?, baseCourseId?, counsellorId? } */
  const fetchRows = (f, s, p, size) => {
    setLoading(true);
    setSelectedKeys([]);
    dispatch(
      getAdminSupport(
        {
          skip: (p - 1) * size,
          limit: size,
          supportType: 1,
          startDate: f.startDate,
          endDate: f.endDate,
          status: s,
          ...(f.supportType?._id && { technicalSupportType: f.supportType._id }),
          ...(f.exam?._id && { examTypeId: f.exam._id }),
          ...(f.examStream?._id && { baseCourseId: f.examStream._id }),
          ...(f.counsellor?._id && { counsellorId: f.counsellor._id }),
        },
        (res) => {
          setRows(res?.data?.data || []);
          setCount(res?.data?.count || 0);
          setLoading(false);
        },
        () => {
          setRows([]);
          setCount(0);
          setLoading(false);
        },
      ),
    );
  };

  useEffect(() => {
    dispatch(getExamListNew((res) => setExamList(res || [])));
    if (filters.exam?._id) dispatch(getParentCourseNew(filters.exam._id, (res) => setStreamList(res || [])));
    dispatch(
      getCRMEmployeeList({ role: SERVICE_COUNSELLOR_ROLE }, (res) => {
        const list = (res?.data || []).map((e) => ({ _id: e?._id, name: [e?.firstName, e?.lastName].filter(Boolean).join(" ") }));
        setCounsellors(list);
        if (fromChat && filters.counsellor?._id) setFilters((f) => ({ ...f, counsellor: list.find((c) => c._id === f.counsellor._id) || f.counsellor }));
      }),
    );
    fetchRows(filters, status, 1, pageSize);
  }, []);

  const openChat = (row) =>
    navigate("../chat", {
      state: {
        id: String(row?._id),
        userId: row?.userId ? row.userId : row?.teacherId,
        status: row?.status,
        screeName: "technicalSupport",
        baseCourseId: { _id: filters.examStream?._id, name: filters.examStream?.name },
        selectedStatus: status,
        examTypeId: { _id: filters.exam?._id, name: filters.exam?.name },
        startDate: filters.startDate,
        endDate: filters.endDate,
        counsellorId: { _id: filters.counsellor?._id, name: filters.counsellor?.name },
      },
    });

  const assign = () => {
    if (!assignTo || !selectedKeys.length) return;
    setAssigning(true);
    dispatch(
      assignSupportIssue(
        { counsellorId: assignTo._id, issueList: selectedKeys }, // same body (the old list kept growing on every click)
        () => {
          setAssigning(false);
          setAssignTo("");
          fetchRows(filters, status, page, pageSize);
        },
        () => setAssigning(false),
      ),
    );
  };

  // same button rules as before: New · Open (in progress, created today) / InProgress · View
  const statusButton = (r) => {
    const today = moment.utc(r?.createdAt).format("DD-MM-YYYY") === moment.utc(new Date()).format("DD-MM-YYYY");
    const label = r?.status === 1 ? "New" : r?.status === 2 ? (today ? "Open" : "InProgress") : r?.status === 3 ? "View" : "";
    if (!label) return "—";
    return (
      <span className="position-relative d-inline-block">
        <CustomButton size="sm" variant={r?.status === 3 ? "outline" : r?.status === 1 ? "primary" : "secondary"} onClick={() => openChat(r)} title="Open this issue's chat">
          {label}
        </CustomButton>
        {r?.unreadMsgCountAdmin > 0 && r?.status !== 3 && (
          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill text-bg-success" title={`${r.unreadMsgCountAdmin} unread messages`}>
            {r.unreadMsgCountAdmin}
          </span>
        )}
      </span>
    );
  };

  const columns = [
    { key: "issueId", header: "Issue ID", minWidth: 110, render: (v) => <strong>{v || "—"}</strong> },
    { key: "createdAt", header: "Issue Date", minWidth: 150, render: (v) => (v ? moment(v).format("DD/MM/YYYY, hh:mm A") : "—") },
    { key: "student", header: "Student Name", minWidth: 140, render: (_, r) => [r?.userId?.firstName, r?.userId?.lastName].filter(Boolean).join(" ") || "—" },
    { key: "mobile", header: "Mobile No", minWidth: 140, render: (_, r) => <MobileCell value={r?.userId?.mobileNo} /> },
    { key: "batch", header: "Batch Name", minWidth: 180, wrap: true, render: (_, r) => <BatchNames names={r?.courseId} /> },
    { key: "name", header: "Issue Name", minWidth: 150, wrap: true },
    { key: "technicalSupportType", header: "Support Type", minWidth: 120, render: (v) => (v ? (v === 1 ? "App Related" : "Course Related") : "—") },
    { key: "issue", header: "Token Type", minWidth: 120 },
    { key: "assignedTo", header: "Issue Assign To", minWidth: 140, render: (_, r) => (r?.issueAssignTo?.firstName ? `${r.issueAssignTo.firstName} ${r.issueAssignTo.lastName || ""}`.trim() : "—") },
    { key: "resolvedBy", header: "Issue Resolved By", minWidth: 140, render: (_, r) => r?.issueResolvedBy?.name || "—" },
    { key: "status", header: "Status", align: "center", minWidth: 120, render: (_, r) => statusButton(r) },
  ];

  const filterFields = useMemo(
    () => [
      { key: "startDate", label: "Start Date", type: "date", half: true, isRequired: true, defaultValue: todayISO },
      { key: "endDate", label: "End Date", type: "date", half: true, isRequired: true, defaultValue: todayISO, minDate: (v) => v.startDate, validate: (value, v) => (value && v.startDate && value < v.startDate ? "End date can't be before the start date" : "") },
      { key: "supportType", label: "Support Type", type: "dropdown", options: SUPPORT_TYPES, search: false },
      { key: "exam", label: "Exam Name", type: "dropdown", options: examList, clears: ["examStream"] },
      { key: "examStream", label: "Exam Stream", type: "dropdown", options: streamList, disabled: (v) => !v.exam, hint: (v) => (!v.exam ? "Select an exam first" : undefined) },
      { key: "counsellor", label: "Counsellor", type: "dropdown", options: counsellors },
    ],
    [examList, streamList, counsellors],
  );

  return (
    <CustomPageLayout title="TECHNICAL SUPPORT">
      <CustomAdminTable
        id="technical-support"
        title={<TableTitle title={`Issues (${count})`} from={filters.startDate} to={filters.endDate} />}
        columns={columns}
        data={rows}
        loading={loading}
        rowKey={(r, i) => r?._id || i}
        enableSerialNumber
        serialHeader="S.No"
        enableSelection={isAdmin}
        selectedRowKeys={selectedKeys}
        onSelectionChange={(keys) => setSelectedKeys(keys)}
        filters={
          <StatusDropdown
            options={STATUSES}
            value={status}
            onChange={(s) => {
              setStatus(s);
              setPage(1);
              fetchRows(filters, s, 1, pageSize);
            }}
          />
        }
        toolbarActions={
          isAdmin &&
          selectedKeys.length > 0 && (
            <div className="d-flex flex-wrap align-items-center gap-2">
              <CustomBadge tone="primary">{selectedKeys.length} selected</CustomBadge>
              <div style={{ minWidth: 200 }}>
                <CustomDropdowns placeholder="Assign To Counsellor" options={counsellors} value={assignTo} onChange={(v) => setAssignTo(v || "")} />
              </div>
              <CustomButton size="sm" icon={<UserPlus size={14} />} onClick={assign} loading={assigning} disabled={!assignTo} title={assignTo ? `Assign the ticked issues to ${assignTo.name}` : "Pick a counsellor first"}>
                Assign Issue
              </CustomButton>
            </div>
          )
        }
        advancedFilters={filterFields}
        advancedFilterValues={filters}
        onApplyFilters={(values) => {
          setFilters(values);
          setPage(1);
          fetchRows(values, status, 1, pageSize);
        }}
        applyFiltersOnReset
        onFilterChange={(key, value) => {
          if (key !== "exam") return;
          setStreamList([]);
          if (value) dispatch(getParentCourseNew(value._id, (res) => setStreamList(res || [])));
        }}
        filterSubtitle="Pick the dates; narrow by support type, exam, stream or counsellor"
        pagination={{
          page,
          pageSize,
          total: count,
          onPageChange: (p) => {
            setPage(p);
            fetchRows(filters, status, p, pageSize);
          },
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPage(1);
            fetchRows(filters, status, 1, size);
          },
        }}
        emptyTitle="No issues"
        emptyDescription={`No ${STATUSES.find((x) => x._id === status)?.name.toLowerCase()} for ${formatDateRange(filters.startDate, filters.endDate)}.`}
        maxHeight="83vh"
      />
    </CustomPageLayout>
  );
};

export default TechnicalSupportNew;
